import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPaperPlane, FaWallet } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';

/**
 * Dépôt d'un acompte sur salaire par son titulaire (Yros/Kavero#410).
 *
 * Un seul champ, et c'est le fond du sujet : un acompte est du **salaire déjà
 * gagné** (L.130 al. 6). Il se retient sur le mois en cours, il n'y a ni durée
 * à proposer, ni motif à justifier, ni reconnaissance de dette à signer — ce
 * qui distingue justement l'acompte du prêt, dont la retenue sans autorisation
 * écrite serait nulle (L.132).
 *
 * Aucune règle métier ici. Le plafond et l'encours vivent dans Odoo, et leurs
 * refus s'affichent mot pour mot : les reformuler ferait diverger deux vérités.
 * L'écran ne vérifie que ce qu'il peut voir — un montant qui n'est pas un
 * nombre strictement positif.
 *
 * Assemblage repris de `DemandeAvance`, son frère de destination.
 */

const CLASSE_CHAMP =
  'mt-1 w-full px-4 py-2 border rounded-lg text-gray-700 bg-gray-50 font-medium text-sm';

function DemandeAcompte({ onDepot }) {
  const { t } = useTranslation();
  const [montant, setMontant] = useState('');
  const [erreur, setErreur] = useState(null);
  const [succes, setSucces] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  const soumettre = async (event) => {
    event.preventDefault();
    setErreur(null);
    setSucces(null);

    // `Number` plutôt que `parseFloat` : « 50 000 » ou « 12abc » doivent être
    // refusés ici, pas partir chez Odoo pour en revenir en 409.
    const montantNombre = Number(montant);
    if (!montant || !Number.isFinite(montantNombre) || montantNombre <= 0) {
      setErreur(t('demandeAcompte.montantInvalide'));
      return;
    }

    setEnvoi(true);
    try {
      const reponse = await apiService.creerDemandeRh({
        type: 'acompte',
        contenu: { montant: montantNombre },
      });
      if (reponse && reponse.ok) {
        setSucces(t('demandeAcompte.succes'));
        setMontant('');
        if (onDepot) onDepot();
      } else {
        setErreur(reponse?.message || t('demandeAcompte.erreurGenerique'));
      }
    } catch (e) {
      // Un refus métier remonte en 409, donc par la branche d'erreur d'axios :
      // c'est là que vit le message d'Odoo, et il doit être affiché tel quel.
      setErreur(e?.response?.data?.message || t('demandeAcompte.erreurGenerique'));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md border border-gray-100 space-y-4">
      <div className="flex items-center gap-2">
        <FaWallet className="text-[--color-primary]" />
        <h2 className="text-base sm:text-lg font-semibold">{t('demandeAcompte.titre')}</h2>
      </div>
      <p className="text-sm text-gray-600">{t('demandeAcompte.explication')}</p>

      <form data-testid="acompte-formulaire" onSubmit={soumettre} className="space-y-4">
        <label className="block text-sm font-medium sm:max-w-xs">
          {t('demandeAcompte.montant')}
          <input
            type="number"
            inputMode="numeric"
            min="1"
            step="any"
            data-testid="acompte-montant"
            value={montant}
            onChange={(e) => setMontant(e.target.value)}
            className={CLASSE_CHAMP}
          />
        </label>

        {erreur && (
          <p
            data-testid="acompte-erreur"
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          >
            {erreur}
          </p>
        )}
        {succes && (
          <p
            data-testid="acompte-succes"
            className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2"
          >
            {succes}
          </p>
        )}

        <div className="flex justify-end pt-6 border-t border-gray-200">
          <button
            type="submit"
            data-testid="acompte-soumettre"
            disabled={envoi}
            className="flex items-center gap-2 px-6 py-3 bg-[--color-primary] text-white rounded-lg hover:bg-[--bg-hover] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <FaPaperPlane size={14} />
            {envoi ? t('demandeAcompte.envoi') : t('demandeAcompte.envoyer')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default DemandeAcompte;
